import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChordTypographicField } from "../chords/ChordTypographicField";
import { ModoPositionSelector } from "./ModoPositionSelector";
import { ModoBrilloSpectrum } from "./ModoBrilloSpectrum";
import { PracticeSurface } from "../practice/PracticeSurface";
import { TaskCreatorButton } from "../TaskCreatorButton";
import { serializeModeState } from "../../lib/taskEngine";
import type { NoteName, NeckPosition } from "../../lib/guitarEngine";
import type { FretboardPattern } from "../../lib/fingeringEngine";
import type { PortalInfo } from "../../lib/modoEngine";
import type { NeckConnection } from "../practice/PracticeSurface";

export interface ModoImmersiveCanvasProps {
  tonica: NoteName;
  modoNombre: string;
  brillo: number;
  notas: NoteName[];
  portal: PortalInfo | null;
  acordeSiglas: string;
  tensionesGrados: string;
  fingerings: readonly FretboardPattern[];
  selectedFingerings: number[];
  onToggleFingering: (index: number) => void;
  onNextTonica: () => void;
  onSelectBrillo: (brillo: number) => void;
  highlightedPositions: NeckPosition[];
  spotlightKeys?: Set<string>;
  activeNote: { string: number; fret: number } | null;
  onFretClick: (stringNum: number, fret: number, note: NoteName) => void;
  connections: NeckConnection[];
  stringOrder: "tab" | "physical";
  leftHanded: boolean;
  onStringOrderToggle: () => void;
  onLeftHandedToggle: () => void;
  pattern: string;
  octaves: number;
  direction: "asc" | "desc" | "both";
}

export const ModoImmersiveCanvas: React.FC<ModoImmersiveCanvasProps> = ({
  tonica,
  modoNombre,
  brillo,
  notas,
  portal,
  acordeSiglas,
  tensionesGrados,
  fingerings,
  selectedFingerings,
  onToggleFingering,
  onNextTonica,
  onSelectBrillo,
  highlightedPositions,
  spotlightKeys,
  activeNote,
  onFretClick,
  connections,
  stringOrder,
  leftHanded,
  onStringOrderToggle,
  onLeftHandedToggle,
  pattern,
  octaves,
  direction,
}) => {
  const fretboardRef = useRef<HTMLDivElement>(null);
  const [boardWidth, setBoardWidth] = useState(1400);
  const title = `${tonica} ${modoNombre}`;

  const measureBoard = useCallback(() => {
    const el = fretboardRef.current;
    if (!el) return;
    setBoardWidth(Math.max(640, el.clientWidth - 16));
  }, []);

  useEffect(() => {
    measureBoard();
    const el = fretboardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measureBoard);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureBoard]);

  const theoryItems = useMemo(() => {
    const items: { label: string; value: string }[] = [];
    if (portal) {
      items.push({ label: "RELATIVO", value: `${portal.nota} Maj` });
    }
    items.push({ label: "TÓNICA", value: acordeSiglas });
    if (tensionesGrados) {
      items.push({ label: "TENSIONES", value: tensionesGrados });
    }
    return items;
  }, [portal, acordeSiglas, tensionesGrados]);

  return (
    <div className="h-[calc(100dvh-5rem)] min-h-0 flex flex-col overflow-hidden overflow-x-hidden w-full max-w-none flex-1 box-border pt-28 px-12">
      <div className="shrink-0 flex justify-between items-baseline w-full border-b border-white/5 pb-6">
        <h1 className="text-7xl font-serif text-white/95 leading-none tracking-tight">{title}</h1>
        <TaskCreatorButton
          type="mode"
          variant="immersive"
          title={`Modo ${title}`}
          description={`Brillo ${brillo > 0 ? `+${brillo}` : brillo}, ${notas.length} notas`}
          config={serializeModeState(tonica, brillo, pattern, octaves, direction)}
        />
      </div>

      <div className="shrink-0 flex flex-row items-center justify-between w-full mt-6 mb-8 gap-12 min-h-[4.5rem]">
        <div className="flex flex-row items-center gap-8 min-w-0 flex-1 overflow-x-auto scrollbar-hide">
          <ChordTypographicField label="Tónica" value={tonica} onClick={onNextTonica} />
          <ModoPositionSelector
            fingerings={fingerings}
            selectedIndices={selectedFingerings}
            onToggle={onToggleFingering}
          />
        </div>

        <div className="flex flex-row items-center gap-6 shrink-0 flex-wrap justify-end max-w-[50%]">
          <div className="flex flex-row flex-wrap items-center gap-3 text-sm font-mono tracking-widest">
            {theoryItems.map((item, i) => (
              <React.Fragment key={item.label}>
                {i > 0 ? <span className="text-white/20 select-none">|</span> : null}
                <span className="whitespace-nowrap">
                  <span className="text-white/30 uppercase text-[10px] tracking-widest mr-2">
                    {item.label}
                  </span>
                  <span className="text-white/80">{item.value}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
          <ModoBrilloSpectrum
            brillo={brillo}
            modoNombre={modoNombre}
            onSelectBrillo={onSelectBrillo}
          />
        </div>
      </div>

      <div ref={fretboardRef} className="flex-1 w-full min-h-0 pb-6 relative flex flex-col">
        <PracticeSurface
          className="w-full h-full min-h-0 max-w-none"
          highlightedPositions={highlightedPositions}
          activeNote={activeNote}
          onFretClick={onFretClick}
          showNoteNames
          showDegrees={false}
          interactive
          stringOrder={stringOrder}
          leftHanded={leftHanded}
          connections={connections}
          focusLevel={3}
          visualStyle="wireframe"
          sizeTier="monumental"
          boardWidthOverride={boardWidth}
          spotlightKeys={spotlightKeys}
          sparseCells={Boolean(spotlightKeys)}
          exploreChordNodes
        />

        <div className="absolute top-2 right-0 flex flex-col gap-1.5 z-10">
          <button
            type="button"
            onClick={onStringOrderToggle}
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans transition-all bg-black/35 backdrop-blur-md border border-white/12 text-slate-100 hover:bg-white/10"
            title={stringOrder === "physical" ? "Orden físico" : "Orden tab"}
          >
            {stringOrder === "physical" ? "|||" : "≡"}
          </button>
          <button
            type="button"
            onClick={onLeftHandedToggle}
            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans transition-all border ${
              leftHanded
                ? "bg-emerald-400/20 text-emerald-200 border-emerald-400/40"
                : "bg-black/35 border-white/12 text-slate-100 hover:bg-white/10"
            }`}
            title={leftHanded ? "Zurdo" : "Diestro"}
          >
            {leftHanded ? "←" : "→"}
          </button>
        </div>
      </div>
    </div>
  );
};
